import { resolveWencesWebhookBaseUrl } from "@/utils/tenantTestEnvironment";

/**
 * Obtiene la URL base del WebSocket (misma API que el front: prod o api2-test).
 */
function getWebSocketBaseUrl() {
  const trimmed = resolveWencesWebhookBaseUrl().replace(/\/$/, "");
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  // Sustituir el esquema completo: `^ws` sobre "wss://" generaba "wsss://".
  return trimmed.replace(/^(wss?|https?):\/\//i, protocol + "//");
}

class WebSocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 3;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.userId = null;
    this.disabled = false;
    this.connecting = false; // Evita múltiples intentos simultáneos
  }

  connect(userId) {
    if (this.disabled) {
      return;
    }

    this.userId = userId;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    // Evitar varios intentos a la vez (varios componentes llaman connect())
    if (this.connecting || (this.ws && this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.attemptConnection();
  }

  attemptConnection() {
    const wsUrl = getWebSocketBaseUrl();

    this.connecting = true;

    try {
      this.ws = new WebSocket(wsUrl);

      const connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
          this.connecting = false;
          this.attemptReconnect();
        }
      }, 5000);

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        this.connecting = false;
        this.isConnected = true;
        this.reconnectAttempts = 0;

        if (this.userId) {
          this.send({ type: "identify", userId: this.userId });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          this.handleMessage(JSON.parse(event.data));
        } catch (error) {
          console.error("WebSocket: Error parsing message:", error);
        }
      };

      this.ws.onclose = () => {
        clearTimeout(connectionTimeout);
        this.connecting = false;
        this.isConnected = false;
        this.attemptReconnect();
      };

      this.ws.onerror = () => {
        clearTimeout(connectionTimeout);
        if (this.reconnectAttempts === 0) {
          console.warn("WebSocket: Connection error. Real-time notifications may not work.");
        }
      };
    } catch (error) {
      console.error("WebSocket: Error creating connection:", error);
      this.connecting = false;
      this.attemptReconnect();
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.disabled = true;
      return;
    }
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    setTimeout(() => {
      if (!this.disabled && !this.isConnected) {
        this.attemptConnection();
      }
    }, delay);
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn("WebSocket not connected, cannot send message");
    }
  }

  handleMessage(message) {
    // Notificar a todos los listeners registrados
    this.listeners.forEach((callback, type) => {
      if (type === message.type || type === "*") {
        callback(message);
      }
    });
  }

  // Registrar un listener para un tipo específico de mensaje
  on(messageType, callback) {
    this.listeners.set(messageType, callback);
  }

  // Remover un listener
  off(messageType) {
    this.listeners.delete(messageType);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.connecting = false;
    this.reconnectAttempts = 0;
    this.disabled = false;
    this.listeners.clear();
  }
}

// Crear una instancia singleton
const websocketService = new WebSocketService();

export default websocketService;
